import type { WorkSheet } from 'xlsx';
import { type ProcessStepRecordingDisplay } from '@bliks-io/contracts';
import {
  buildProcessStepsSheetRows,
  STEPS_NAME_COLUMN_INDEX,
  type BuildStepsOptions,
  type CellValue
} from './processStepsExcel';
import { buildProcessEdgeRows, type ProcessEdgesSheetLabels } from './processEdges';

/**
 * Assembles the Process Detail Excel export: a two-tab workbook of `Schritte`
 * (nodes, one row per step) + `Verzweigungen` (edges, one row per outgoing
 * path), linked by `Schritt Nr.`. This is where SheetJS is used, so the ~300 KB
 * library is loaded on demand (`downloadProcessExcel`) and stays out of the
 * initial bundle.
 */

// Excel sheet name in a formula must be single-quoted when it contains spaces
// or other special characters; always quoting (and doubling embedded quotes)
// is valid and keeps the reference robust against localized tab names.
export function quoteSheetName(name: string): string {
  return `'${name.replace(/'/g, "''")}'`;
}

function setLookupCell(sheet: WorkSheet, address: string, formula: string): void {
  const existing = sheet[address] as { v?: unknown } | undefined;
  const cached = existing && typeof existing.v === 'string' ? existing.v : '';
  // Keep the resolved name as the cached value so the cell shows immediately;
  // Excel replaces it via the formula on recalculation.
  sheet[address] = { t: 's', f: formula, v: cached };
}

/**
 * Overlay VLOOKUP formulas on the branches sheet's name columns: `Prozessschritt`
 * (B) pulls the source step's name from the steps sheet by `Schritt Nr.` (A);
 * `Folgeschritt` (F) pulls the successor's name by `Folgeschritt Nr.` (E). Both
 * read the step-name column (`nameColumnIndex`, 1-based) of `lookupRange` — the
 * caller derives both from the steps sheet so they can't drift from its layout.
 * `rowCount` is the total row count including the header, so data rows are
 * 2..rowCount.
 *
 * The lookup keys on `Schritt Nr.`, which the export assumes is present and
 * unique on every step (guaranteed by the backend's `assignStepNumbers`); a
 * missing or duplicate number would resolve to "" or the first match on recalc.
 */
export function injectEdgeVlookups(
  sheet: WorkSheet,
  rowCount: number,
  lookupRange: string,
  nameColumnIndex: number
): void {
  for (let row = 2; row <= rowCount; row++) {
    setLookupCell(
      sheet,
      `B${row}`,
      `IFERROR(VLOOKUP($A${row},${lookupRange},${nameColumnIndex},FALSE),"")`
    );
    setLookupCell(
      sheet,
      `F${row}`,
      `IFERROR(VLOOKUP($E${row},${lookupRange},${nameColumnIndex},FALSE),"")`
    );
  }
}

function setAutofilter(XLSX: typeof import('xlsx'), sheet: WorkSheet, rows: CellValue[][]): void {
  if (rows.length === 0 || rows[0].length === 0) return;
  sheet['!autofilter'] = {
    ref: XLSX.utils.encode_range({
      s: { r: 0, c: 0 },
      e: { r: rows.length - 1, c: rows[0].length - 1 }
    })
  };
}

// Excel forbids these characters in worksheet names and caps the length at 31.
function sanitizeSheetName(name: string, fallback: string): string {
  return (
    name
      .replace(/[/\\:*?[\]]/g, '-')
      .trim()
      .slice(0, 31) || fallback
  );
}

function sanitizeFileName(name: string): string {
  return name.replace(/[/\\:*?"<>|]/g, '-').trim() || 'process';
}

export interface DownloadProcessExcelOptions extends BuildStepsOptions {
  steps: ProcessStepRecordingDisplay[];
  processName: string;
  /** Worksheet tab name for the steps (nodes) sheet, e.g. "Schritte". */
  stepsSheetName: string;
  /** Worksheet tab name for the branches (edges) sheet, e.g. "Verzweigungen". */
  edgesSheetName: string;
  edgeLabels: ProcessEdgesSheetLabels;
}

/**
 * Build both sheets and trigger a browser download. SheetJS is imported on
 * demand so it never enters the initial bundle.
 */
export async function downloadProcessExcel(options: DownloadProcessExcelOptions): Promise<void> {
  const { steps, processName, stepsSheetName, edgesSheetName, edgeLabels } = options;

  const stepsResult = buildProcessStepsSheetRows(steps, options);
  const edgesResult = buildProcessEdgeRows(steps, { labels: edgeLabels });

  const XLSX = await import('xlsx');

  const stepsSheet = XLSX.utils.aoa_to_sheet(stepsResult.rows);
  stepsSheet['!cols'] = stepsResult.colWidths;
  setAutofilter(XLSX, stepsSheet, stepsResult.rows);

  const edgesSheet = XLSX.utils.aoa_to_sheet(edgesResult.rows);
  edgesSheet['!cols'] = edgesResult.colWidths;
  setAutofilter(XLSX, edgesSheet, edgesResult.rows);

  const stepsTab = sanitizeSheetName(stepsSheetName, 'Schritte');
  // Derive the lookup range end from the steps sheet's actual width so it tracks
  // the metrics toggle (13 vs 17 columns) instead of a hardcoded last column.
  const lastStepsColumn = XLSX.utils.encode_col(Math.max(stepsResult.rows[0]?.length ?? 1, 1) - 1);
  const lookupRange = `${quoteSheetName(stepsTab)}!$A:$${lastStepsColumn}`;
  injectEdgeVlookups(edgesSheet, edgesResult.rows.length, lookupRange, STEPS_NAME_COLUMN_INDEX);

  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, stepsSheet, stepsTab);
  XLSX.utils.book_append_sheet(
    workbook,
    edgesSheet,
    sanitizeSheetName(edgesSheetName, 'Verzweigungen')
  );

  const date = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(workbook, `${sanitizeFileName(processName)} - ${date}.xlsx`);
}
